import { mediaQueryLarge } from '@theme/utilities';

/**
 * Shows at most `desktop-limit` / `mobile-limit` swatch `<li>` items; remaining count as +X.
 * Limit <= 0 or empty string (when attribute is set) means show all — no +more.
 */
export class OverflowOptions extends HTMLElement {
  #userExpandedAll = false;

  #onViewportChange = () => this.#layout();

  /**
   * @param {string} attrName
   * @param {number} whenAbsent - used only if `attrName` is not present on the element
   * @returns {number} positive = cap; 0 = unlimited for that breakpoint
   */
  #effectiveLimit(attrName, whenAbsent) {
    if (!this.hasAttribute(attrName)) return whenAbsent;
    const raw = this.getAttribute(attrName)?.trim() ?? '';
    if (raw === '' || raw === '0') return 0;
    const limit = parseInt(raw, 10);
    if (!Number.isFinite(limit) || limit < 1) return 0;
    return limit;
  }

  connectedCallback() {
    const swatchHost = this.closest('product-swatches');
    // Only propagate non-empty host values. An empty string on our own
    // attribute would be treated as "unlimited" by #effectiveLimit, silently
    // overriding the merchant-facing defaults (10 / 8).
    const hostDesktop = swatchHost?.getAttribute('data-options-swatch-desktop')?.trim() ?? '';
    if (hostDesktop !== '' && !this.hasAttribute('desktop-limit')) {
      this.setAttribute('desktop-limit', hostDesktop);
    }
    const hostMobile = swatchHost?.getAttribute('data-options-swatch-mobile')?.trim() ?? '';
    if (hostMobile !== '' && !this.hasAttribute('mobile-limit')) {
      this.setAttribute('mobile-limit', hostMobile);
    }

    const card = this.closest('product-card');
    if (card?.getAttribute('data-options-swatches-expanded') === 'true') {
      this.#userExpandedAll = true;
    }
    if (!this.hasAttribute('data-overflow-pending')) {
      this.setAttribute('data-overflow-pending', '');
    }
    mediaQueryLarge.addEventListener('change', this.#onViewportChange);
    this.addEventListener('change', this.#onSwatchChange);

    requestAnimationFrame(() => {
      this.#layout();
    });
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#onViewportChange);
    this.removeEventListener('change', this.#onSwatchChange);
  }

  #onSwatchChange = () => {
    queueMicrotask(() => this.#layout());
  };

  #layout() {
    try {
      if (this.#userExpandedAll) {
        this.#applyExpandedAll();
        return;
      }

      const list = this.querySelector('.overflow-options__list');
      const moreItem = this.querySelector('[data-overflow-options-more]');
      if (!list) return;

      const items = Array.from(
        list.querySelectorAll(':scope > li:not([data-overflow-options-more])'),
        (el) => /** @type {HTMLElement} */ (el),
      );

      const limitDesktop = this.#effectiveLimit('desktop-limit', 10);
      const limitMobile = this.#effectiveLimit('mobile-limit', 8);
      const limit = mediaQueryLarge.matches ? limitDesktop : limitMobile;

      for (const li of items) {
        li.hidden = false;
      }
      if (moreItem instanceof HTMLElement) {
        moreItem.hidden = true;
      }

      if (limit <= 0) {
        this.style.setProperty('--overflow-count', '0');
        return;
      }

      if (items.length <= limit) {
        this.style.setProperty('--overflow-count', '0');
        return;
      }

      const checkedIdx = items.findIndex((li) => li.querySelector('input[type="radio"]:checked'));
      /** @type {Set<number>} */
      const visible = new Set();
      if (checkedIdx >= 0) visible.add(checkedIdx);
      for (let i = 0; i < items.length && visible.size < limit; i++) {
        if (!visible.has(i)) visible.add(i);
      }

      for (let i = 0; i < items.length; i++) {
        const li = items[i];
        if (!li || visible.has(i)) continue;
        li.hidden = true;
      }

      const extra = items.length - limit;
      this.style.setProperty('--overflow-count', String(extra));
      if (moreItem instanceof HTMLElement) {
        moreItem.hidden = false;
      }
    } finally {
      this.removeAttribute('data-overflow-pending');
    }
  }

  #applyExpandedAll() {
    const list = this.querySelector('.overflow-options__list');
    const moreItem = this.querySelector('[data-overflow-options-more]');
    if (!list) return;
    for (const li of list.querySelectorAll(':scope > li:not([data-overflow-options-more])')) {
      if (li instanceof HTMLElement) li.hidden = false;
    }
    if (moreItem instanceof HTMLElement) moreItem.hidden = true;
    this.style.setProperty('--overflow-count', '0');
    this.removeAttribute('data-overflow-pending');
  }

  showAll() {
    this.#userExpandedAll = true;
    this.closest('product-card')?.setAttribute('data-options-swatches-expanded', 'true');
    this.#applyExpandedAll();
  }

  refresh() {
    this.#layout();
  }
}

if (!customElements.get('overflow-options')) {
  customElements.define('overflow-options', OverflowOptions);
}
